<script setup lang="ts">
import { Link, router } from "@inertiajs/vue3"
import { reactive, ref } from "vue"
import AppShell from "../Layout/AppShell.vue"
import { UiButton, UiDataTable } from "@/components"
import { useConfirmation } from "@/composables/useConfirmation"
import { useNotifications } from "@/composables/useNotifications"
import type { UiDataTableColumn, UiDataTableFilter } from "@/types/ui"

const { confirm } = useConfirmation()
const notify = useNotifications()

type ProductRow = {
  id: number
  company: string
  name: string
  sku: string
  kind: string
  unit: string
  cost_price: string
  purchasable: string
  can_edit: boolean
  can_destroy: boolean
}

const props = defineProps<{
  products: ProductRow[]
  companies: Array<{ id: number; name: string }>
  query: {
    q?: string
    company_id?: string
    page: number
    per_page: number
  }
  pagination: {
    page: number
    perPage: number
    total: number
  }
  can_create: boolean
}>()

const columns: UiDataTableColumn[] = [
  { key: "company", label: "Empresa" },
  { key: "sku", label: "SKU" },
  { key: "name", label: "Produto" },
  { key: "kind", label: "Tipo" },
  { key: "unit", label: "Unidade", align: "center" },
  { key: "cost_price", label: "Custo padrao", align: "right" },
  { key: "purchasable", label: "Compravel", align: "center" },
  { key: "actions", label: "Acoes", align: "right" },
]

const filters: UiDataTableFilter[] = [
  {
    key: "company_id",
    label: "Empresa",
    options: props.companies.map((company) => ({ label: company.name, value: company.id })),
  },
]

const selectedFilters = reactive<Record<string, string | number | "">>({
  company_id: props.query.company_id ?? "",
})

const fileInput = ref<HTMLInputElement | null>(null)
const isImporting = ref(false)

const IMPORT_ERRORS_PREVIEW_LIMIT = 20

function fetchWithQuery(overrides: Record<string, string | number | ""> = {}) {
  const params = {
    q: String(overrides.q ?? props.query.q ?? ""),
    company_id: String(overrides.company_id ?? selectedFilters.company_id ?? ""),
    page: Number(overrides.page ?? props.pagination.page),
    per_page: Number(overrides.per_page ?? props.pagination.perPage),
  }

  router.get("/products", params, {
    preserveState: true,
    preserveScroll: true,
    replace: true,
  })
}

function updateSearch(value: string) {
  fetchWithQuery({ q: value, page: 1 })
}

function updateFilter({ key, value }: { key: string; value: string | number | "" }) {
  selectedFilters[key] = value
  fetchWithQuery({ [key]: value, page: 1 })
}

function changePage(page: number) {
  fetchWithQuery({ page })
}

function changePerPage(perPage: number) {
  fetchWithQuery({ per_page: perPage, page: 1 })
}

async function downloadTemplate() {
  const ok = await confirm({
    title: "Instrucoes da planilha de importacao",
    message: "Confira as instrucoes antes de baixar o template.",
    contentKey: "products-import-template",
    type: "info",
    confirmText: "Baixar template",
  })
  if (!ok) return

  window.open("/products/download_template", "_blank")
}

function triggerImport() {
  if (!selectedFilters.company_id) {
    notify.warning("Selecione uma empresa para importar os produtos")
    return
  }

  fileInput.value?.click()
}

function buildImportErrorMessage(errors: string[]): string {
  const preview = errors.slice(0, IMPORT_ERRORS_PREVIEW_LIMIT).map((line) => `- ${line}`)
  const remaining = errors.length - preview.length

  if (remaining > 0) {
    preview.push(`- ... e mais ${remaining} erro(s).`)
  }

  return [
    `A importacao encontrou ${errors.length} erro(s).`,
    "",
    ...preview,
  ].join("\n")
}

async function onImportFileChange(event: Event) {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]

  if (!file) return

  isImporting.value = true

  try {
    const formData = new FormData()
    formData.append("file", file)
    formData.append("company_id", String(selectedFilters.company_id))

    const response = await fetch("/products/import", {
      method: "POST",
      headers: {
        "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "",
      },
      body: formData,
    })

    if (!response.ok) {
      const payload = await response.json()
      if (Array.isArray(payload.errors) && payload.errors.length > 0) {
        await confirm({
          title: "Falha na importacao",
          message: buildImportErrorMessage(payload.errors),
          type: "warning",
          confirmText: "Entendi",
          cancelText: "Fechar",
        })
      } else {
        notify.error("Nao foi possivel importar a planilha")
      }
      return
    }

    const payload = await response.json()
    notify.success(`${payload.created_count || 0} produto(s) importado(s) com sucesso`)
    fetchWithQuery({ page: 1 })
  } catch {
    notify.error("Falha de comunicacao ao importar a planilha")
  } finally {
    isImporting.value = false
    target.value = ""
  }
}

async function destroyProduct(id: number) {
  const ok = await confirm({
    title: "Excluir produto",
    message: "Deseja excluir este produto?",
    type: "danger",
    confirmText: "Excluir",
  })
  if (!ok) return

  router.delete(`/products/${id}`, {
    preserveScroll: true,
    onSuccess: () => notify.success("Produto excluido com sucesso"),
  })
}
</script>

<template>
  <AppShell>
    <div class="space-y-6">
      <section class="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <h1 class="text-2xl font-semibold text-slate-900">Produtos</h1>
        <p class="mt-2 text-sm text-slate-600">Gerencie produtos por empresa com SKU, unidade, custo e status de compra.</p>
      </section>

      <UiDataTable
        :columns="columns"
        :rows="products"
        :search="query.q ?? ''"
        :filters="filters"
        :selected-filters="selectedFilters"
        :pagination="pagination"
        @update:search="updateSearch"
        @update:filter="updateFilter"
        @page-change="changePage"
        @per-page-change="changePerPage"
      >
        <template #toolbar-actions>
          <div class="flex items-center gap-2">
            <UiButton size="sm" variant="secondary" @click="downloadTemplate">
              Baixar template
            </UiButton>

            <UiButton size="sm" variant="secondary" :disabled="isImporting" @click="triggerImport">
              {{ isImporting ? "Importando..." : "Importar excel" }}
            </UiButton>

            <input ref="fileInput" type="file" accept=".xlsx" class="hidden" @change="onImportFileChange" />
          </div>

          <Link
            v-if="can_create"
            href="/products/new"
            class="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800"
          >
            Novo produto
          </Link>
        </template>

        <template #cell="{ row, column, value }">
          <div v-if="column.key === 'actions'" class="flex justify-end gap-2">
            <Link
              v-if="(row as ProductRow).can_edit"
              :href="`/products/${(row as ProductRow).id}/supplier_prices`"
              class="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-100"
            >
              Custos fornecedor
            </Link>

            <Link
              v-if="(row as ProductRow).can_edit"
              :href="`/products/${(row as ProductRow).id}/edit`"
              class="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-100"
            >
              Editar
            </Link>

            <UiButton v-if="(row as ProductRow).can_destroy" size="sm" variant="danger" @click="destroyProduct((row as ProductRow).id)">
              Excluir
            </UiButton>
          </div>

          <span v-else>{{ value || "-" }}</span>
        </template>
      </UiDataTable>
    </div>
  </AppShell>
</template>

